import React from 'react';
import Select from 'react-select';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
  displayedCount?: number; // Optional: actual number of items displayed on current page
}

/**
 * Reusable pagination component with page size selector
 * Matches design: Left side shows "Showing results" + dropdown + "of {totalCount}"
 * Right side shows "Page" + navigation buttons + "of {totalPages}"
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  className = '',
  displayedCount,
}) => {
  // Calculate the correct range of items being displayed
  // If displayedCount is provided, use it to calculate accurate endItem
  // Otherwise, use standard calculation based on totalCount
  const startItem = totalCount > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
  const endItem = displayedCount !== undefined && displayedCount > 0
    ? (currentPage - 1) * pageSize + displayedCount
    : (totalCount > 0 ? Math.min(currentPage * pageSize, totalCount) : 0);
  
  // Filter page size options dynamically based on totalCount
  // Show all standard options <= totalCount, plus include totalCount if it's between options
  // Always include current pageSize even if it exceeds totalCount (to avoid breaking UI)
  let availableOptions: number[] = [];
  
  if (totalCount > 0) {
    // Get all standard options that are <= totalCount
    const standardOptionsWithinLimit = pageSizeOptions.filter(size => size <= totalCount);
    availableOptions = [...standardOptionsWithinLimit];
    
    // If totalCount is greater than the max standard option but less than the next standard option,
    // add totalCount as an option
    // Example: totalCount = 42, standard options = [10, 20, 50, 100]
    // maxStandardOption = 20, nextStandardOption = 50
    // So add 42: [10, 20, 42]
    const maxStandardOption = standardOptionsWithinLimit.length > 0 
      ? Math.max(...standardOptionsWithinLimit)
      : 0;
    const nextStandardOption = pageSizeOptions.find(size => size > totalCount);
    
    if (totalCount > maxStandardOption && (!nextStandardOption || totalCount < nextStandardOption)) {
      if (!availableOptions.includes(totalCount)) {
        availableOptions.push(totalCount);
      }
    }
    
    // Always ensure current pageSize is included (even if it exceeds totalCount)
    if (!availableOptions.includes(pageSize)) {
      availableOptions.push(pageSize);
    }
  } else {
    // If totalCount is 0, just show current pageSize
    availableOptions = [pageSize];
  }
  
  // Sort and remove duplicates
  availableOptions = Array.from(new Set(availableOptions)).sort((a, b) => a - b);

  // Convert to react-select format
  const pageSizeSelectOptions = availableOptions.map(size => ({
    value: size,
    label: size.toString()
  }));

  const currentPageSizeOption = pageSizeSelectOptions.find(opt => opt.value === pageSize) || pageSizeSelectOptions[0];

  const handlePageSizeChange = (selectedOption: any) => {
    if (selectedOption) {
      const newPageSize = selectedOption.value;
      onPageSizeChange(newPageSize);
      // Reset to page 1 when page size changes
      if (currentPage !== 1) {
        onPageChange(1);
      }
    }
  };

  // React-select styles matching design specifications
  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
      ...base,
      width: '70px',
      minWidth: '70px',
      height: '36px',
      minHeight: '36px',
      border: '0.5px solid #d0d0d0',
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      fontWeight: '400',
      fontStyle: 'normal',
      lineHeight: '16px',
      letterSpacing: '0%',
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      borderColor: state.isFocused ? '#0077F9' : '#d0d0d0',
      padding: '0',
      cursor: 'pointer',
      '&:hover': {
        borderColor: state.isFocused ? '#0077F9' : '#999999'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }),
    input: (base: any) => ({
      ...base,
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0',
      height: 'auto',
      fontSize: '12px',
      lineHeight: '16px'
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px 0 0'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0',
      svg: {
        width: '12px',
        height: '12px',
        color: '#333333'
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#333333',
      fontSize: '12px',
      lineHeight: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      fontWeight: '400',
      fontStyle: 'normal',
      letterSpacing: '0%',
      margin: '0',
      padding: '0'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#0077F9'
        : state.isFocused 
          ? '#f8f9fa'
          : 'white',
      color: state.isSelected ? '#ffffff' : '#212529',
      fontSize: '12px',
      lineHeight: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      fontWeight: '400',
      padding: '8px 12px',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0077F9' : '#f8f9fa',
        color: state.isSelected ? '#ffffff' : '#212529'
      }
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '4px',
      border: '0.5px solid #d0d0d0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginTop: '4px',
      overflow: 'hidden'
    })
  };

  const getPageNumbers = () => {
    const maxVisiblePages = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of middle range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(5, totalPages - 1);
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 4);
        endPage = totalPages - 1;
      }

      // Add ellipsis before middle range if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }

      // Add middle range pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis after middle range if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className={`pagination-wrapper ${className}`}>
      <div className="pagination-container">
        {/* Left Side: Showing results range */}
        <div className="pagination-left">
          <span className="pagination-label">Showing results</span>
          <div className="pagination-page-size-select-wrapper">
            <Select
              classNamePrefix="react-select"
              value={currentPageSizeOption}
              onChange={handlePageSizeChange}
              options={pageSizeSelectOptions}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={selectStyles}
              aria-label="Select page size"
            />
          </div>
          <span className="pagination-label">of {totalCount}</span>
        </div>

        {/* Right Side: Page navigation */}
        <div className="pagination-right">
          <span className="pagination-label">Page</span>
          <div className="pagination-buttons">
            <button
              className="pagination-button pagination-button-nav"
              onClick={handlePrevious}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <i className="ph-caret-left" />
            </button>

            {getPageNumbers().map((pageNum, index) => {
              if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
                return (
                  <button
                    key={`ellipsis-${index}`}
                    className="pagination-button pagination-button-ellipsis"
                    disabled
                    aria-hidden="true"
                  >
                    ...
                  </button>
                );
              }

              const page = pageNum as number;
              const isActive = page === currentPage;

              return (
                <button
                  key={page}
                  className={`pagination-button pagination-button-number ${isActive ? 'active' : ''}`}
                  onClick={() => handlePageClick(page)}
                  aria-label={`Go to page ${page}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {page}
                </button>
              );
            })}

            <button
              className="pagination-button pagination-button-nav"
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <i className="ph-caret-right" />
            </button>
          </div>
          <span className="pagination-label">of {totalPages}</span>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
